import type { AxiosError, AxiosResponse } from "axios";
import type {
  BillingTemplatesPage,
  GetBillingTemplateInput,
  GetBillingTemplatesPageInput,
  GetQueryProps,
  GetTypeSpecificBillingTemplateInput,
} from "shared";
import { axiosClient, fetchCall } from "utilz";
import {
  apiErrorHandler,
  generateUrlString,
  getMspId,
  getQueryDataObject,
} from "utilz/helpers";

export const getAllBillingTemplate = async ({
  perPageCount,
  pageNumber,
  sortColumn,
  sortDirection,
  filter,
  searchQuery,
}: GetQueryProps) => {
  try {
    const data = await getQueryDataObject({
      perPageCount,
      pageNumber,
      sortColumn,
      sortDirection,
      // filter,
      // searchQuery,
    });
    const url = await generateUrlString({
      baseUrl: "/v1/configuration/billing/mail-templates",
      data,
    });
    const response = await axiosClient.get(`${url}`);
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    } else {
      throw response;
    }
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const getAllBillingTemplateV2 = async ({
  perPageCount,
  pageNumber,
  sortColumn,
  sortDirection,
  filter,
  searchQuery,
  customer_id,
}: GetBillingTemplateInput) => {
  try {
    const data = await getQueryDataObject({
      perPageCount,
      pageNumber,
      sortColumn,
      sortDirection,
      // filter,
      // searchQuery,
    });
    if (customer_id) {
      // Fix: Only add customer_id if it is a valid API query param for this endpoint,
      // and only if defined. But ensure data type is compatible and avoid unnecessary mutation.
      if (customer_id !== undefined && customer_id !== null) {
        (data as Record<string, unknown>).customer_id = customer_id;
      }
      const url = await generateUrlString({
        baseUrl: "/v2/configuration/billing/mail-templates",
        data,
      });
      const response = await axiosClient.get(`${url}`);
      const resData = response.data;
      if (resData.success && resData.statusCode === 200) {
        return resData.data;
      } else {
        throw response;
      }
    }
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const getAllTypeSpecificBillingTemplate = async ({
  perPageCount,
  pageNumber,
  sortColumn,
  sortDirection,
  type,
  filter,
  searchQuery,
}: GetTypeSpecificBillingTemplateInput) => {
  try {
    const data = await getQueryDataObject({
      perPageCount,
      pageNumber,
      sortColumn,
      sortDirection,
      // filter,
      searchQuery,
    });
    const url = await generateUrlString({
      baseUrl: `/v1/configuration/billing/${type}`,
      data,
    });
    const response = await axiosClient.get(`${url}`);
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    } else {
      throw response;
    }
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const createBillingMailTemplate = async ({ type, ...data }: any) => {
  try {
    const url = `/v1/configuration/billing/${type}`;
    const response = await axiosClient.post(url, data);
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    } else {
      throw response;
    }
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const deleteBillingTemplate = async (data: {
  invoice_template_ids: string[];
  type: string;
}) => {
  try {
    const url = `/v1/configuration/billing/${data.type}`;
    const response = await axiosClient.put(url, {
      invoice_template_ids: data.invoice_template_ids,
    });
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    } else {
      throw response;
    }
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const updateBillingMailTemplate = async ({ type, ...data }: any) => {
  try {
    const url = `/v1/configuration/billing/${type}/${data.id}`;
    const response = await axiosClient.patch(url, data);
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    } else {
      throw response;
    }
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

/**
 * Both template types in one paginated list (BT-1). Rows are lightweight — see
 * `BillingTemplateListItem`. `filters` is a JSON object (`{"type": [...]}`);
 * an empty `type` array returns both types.
 */
export const getBillingTemplates = async ({
  types,
  searchQuery,
  pageNumber,
  perPageCount,
}: GetBillingTemplatesPageInput): Promise<BillingTemplatesPage> => {
  try {
    const data = getQueryDataObject({
      perPageCount,
      pageNumber,
      sortColumn: "template_name",
      sortDirection: "asc",
      searchQuery,
      filter: { type: types },
    });
    const url = await generateUrlString({
      baseUrl: "/v1/configuration/billing/templates",
      data,
    });
    const response = await axiosClient.get(url);
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    }
    throw response;
  } catch (error) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};
